import { CommentFragmentFragment } from "src/api/graphql/__generated__/graphql";
import EventTopic from "src/helpers/EventTopic";
import getRandomString from "src/helpers/getRandomString";
import { createStore } from "zustand";

type CommentIdType = string;
type OptimisticCommentIdType = string;
export interface CommentsThreadStoreType {
	threadSpecifier: {
		gatheringId?: string;
		photoId?: string;
	};
	displayMessageThreads: boolean;
	optimisticCommentLookup: Record<OptimisticCommentIdType, CommentFragmentFragment>;
	createOptimisticComment: (comment: Omit<CommentFragmentFragment, "commentId">) => {
		deleteOptimisticComment: () => void;
		migrateOptimisticCommentId: (newId: string) => void;
	};
	deleteOptimisticComment: (commentId: string) => void;
	optimisticKeyLookup: Record<CommentIdType, OptimisticCommentIdType>;
	/**
	 * Retrieves the optimistic key lookup. Used to ensure previously optimistic comments can retain the same key
	 */
	getOptimisticKeyLookup: () => Record<CommentIdType, OptimisticCommentIdType>;
	safeArea: {
		top: number;
		bottom: number;
	};
	isAdmin: boolean;
	title: string;
	topic: EventTopic<{
		onPhotoThreadPress: {
			props: {
				photoId: string;
			};
		};
		onCreateCommentSuccess: {
			props: {
				commentId: string;
				threadSpecifier: CommentsThreadStoreType["threadSpecifier"];
			};
		};
		onNewCommentsPolled: {
			props: {};
		};
		onTextInputFocus: {
			props: {};
		};
	}>;
	onClose?: () => void;
	onBack?: () => void;
	getUserTagSuggestions?: (props: {
		/**
		 * The search query to filter user suggestions.
		 */
		query: string;
		/**
		 * The maximum number of user suggestions that can be displayed.
		 */
		limit: number;
		gatheringId?: string;
		photoId?: string;
	}) => Promise<
		{
			userId: string;
			firstName: string;
			lastName: string;
		}[]
	>;
}

export const createCommentsThreadStore = (
	initialState: Pick<
		CommentsThreadStoreType,
		| "threadSpecifier"
		| "displayMessageThreads"
		| "title"
		| "safeArea"
		| "isAdmin"
		| "onClose"
		| "onBack"
		| "getUserTagSuggestions"
	>,
) => {
	return createStore<CommentsThreadStoreType>()((set, get) => ({
		...initialState,
		optimisticCommentLookup: {},
		topic: new EventTopic(),
		optimisticKeyLookup: {},
		getOptimisticKeyLookup: () => {
			return get().optimisticKeyLookup;
		},
		createOptimisticComment: (comment) => {
			let optimisticCommentId = getRandomString();
			set((state) => ({
				optimisticCommentLookup: {
					...state.optimisticCommentLookup,
					[optimisticCommentId]: {
						...comment,
						commentId: optimisticCommentId,
					},
				},
			}));
			return {
				deleteOptimisticComment: () => {
					set((state) => {
						const newLookup = { ...state.optimisticCommentLookup };
						delete newLookup[optimisticCommentId];
						return { optimisticCommentLookup: newLookup };
					});
				},
				migrateOptimisticCommentId: (newId: string) => {
					set((state) => {
						const newLookup = { ...state.optimisticCommentLookup };

						newLookup[newId] = {
							...(newLookup[optimisticCommentId] ?? comment),
							commentId: newId,
						};
						delete newLookup[optimisticCommentId];

						// Doesn't need to be reactive
						state.optimisticKeyLookup[newId] = optimisticCommentId;

						// Updating the optimisticCommentId to the newId
						optimisticCommentId = newId;

						return {
							optimisticCommentLookup: newLookup,
							optimisticKeyLookup: state.optimisticKeyLookup,
						};
					});
				},
			};
		},
		deleteOptimisticComment: (commentId: string) => {
			set((state) => {
				const newLookup = { ...state.optimisticCommentLookup };
				delete newLookup[commentId];
				return { optimisticCommentLookup: newLookup };
			});
		},
	}));
};
